
'use client';

let audioCtx: AudioContext | null = null;

const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
};

type ToneType = 'sine' | 'square' | 'sawtooth' | 'triangle';

const playTone = (
  frequency: number,
  duration: number, // in ms
  type: ToneType = 'sine',
  volume: number = 0.5,
  delay: number = 0
) => {
  const ctx = getAudioContext();
  if (!ctx || !isFinite(frequency) || frequency <= 0) return;
  
  if (ctx.state === 'suspended') {
      ctx.resume();
  }

  const oscillator = ctx.createOscillator();
  const gainNode = ctx.createGain();

  const now = ctx.currentTime;
  const startTime = now + delay / 1000;
  const fadeOutTime = 0.05; 
  const toneDuration = duration / 1000;
  const sustainDuration = toneDuration - fadeOutTime;

  gainNode.gain.setValueAtTime(0, now);
  gainNode.gain.linearRampToValueAtTime(volume, startTime);
  
  if (sustainDuration > 0) {
    gainNode.gain.setValueAtTime(volume, startTime + sustainDuration);
  }

  gainNode.gain.exponentialRampToValueAtTime(0.00001, startTime + toneDuration);

  oscillator.type = type;
  oscillator.frequency.setValueAtTime(frequency, startTime);

  oscillator.connect(gainNode);
  gainNode.connect(ctx.destination);

  oscillator.start(startTime);
  oscillator.stop(startTime + toneDuration);
};

export const playLaserBeep = () => {
    // First chord (C4 Major)
    [261.63, 329.63, 392.00].forEach(freq => {
        playTone(freq, 200, 'triangle', 0.6, 0);
    });

    // Second, higher-pitched chord (C5 Major) after a 25ms break
    [523.25, 659.25, 783.99].forEach(freq => {
        playTone(freq, 150, 'triangle', 0.6, 225);
    });
}

export const playCountdownBeep = () => {
    // C Major chord
    const chord = [523.25, 659.25, 783.99]; // C5, E5, G5
    chord.forEach(freq => playTone(freq, 150, 'square', 0.4));
};

export const playGoBeep = () => {
    const volume = 0.5;
    const waveType: ToneType = 'sawtooth';

    const notes = {
        C_m_low: [130.81, 155.56, 196.00], // C3, Eb3, G3
        G_maj: [196.00, 246.94, 293.66],   // G3, B3, D4
        C_maj_high: [523.25, 659.25, 783.99], // C5, E5, G5
    };

    const sequence = [
        { chord: notes.C_m_low, duration: 150, delay: 0 },
        { chord: notes.G_maj, duration: 150, delay: 150 },
        { chord: notes.C_maj_high, duration: 400, delay: 300 },
    ];
    
    sequence.forEach(item => {
        item.chord.forEach(freq => {
            playTone(freq, item.duration, waveType, volume, item.delay);
        });
    });
};

export const playStationarySound = () => {
    const volume = 0.25;
    const waveType: ToneType = 'sawtooth';
    const notes = {
        C3: 130.81,
        Eb3: 155.56,
        G3: 196.00,
        C4: 261.63,
        Eb4: 311.13,
        G4: 392.00,
    };
    
    // Longer descending arpeggio that slows down
    const sequence = [
        { note: notes.G4, duration: 150, delay: 0 },
        { note: notes.Eb4, duration: 150, delay: 250 },
        { note: notes.C4, duration: 150, delay: 500 },
        { note: notes.G3, duration: 150, delay: 750 },
        { note: notes.Eb3, duration: 150, delay: 1000 },
        { note: notes.C3, duration: 400, delay: 1300 },
    ];

    sequence.forEach(item => {
        playTone(item.note, item.duration, waveType, volume, item.delay);
    });
};

export const playMarkerStartSound = () => {
    // First chord (G-Major)
    [392.00, 493.88, 587.33].forEach(freq => {
        playTone(freq, 150, 'triangle', 0.5, 0);
    });
    // Second, higher chord (D-Major)
    [587.33, 739.99, 880.00].forEach(freq => {
        playTone(freq, 100, 'triangle', 0.5, 175);
    });
};

export const playMarkerEndSound = () => {
    // First chord (C-Major)
    [523.25, 659.25, 783.99].forEach(freq => {
        playTone(freq, 150, 'triangle', 0.5, 0);
    });
    // Second, higher chord (G-Major)
    [783.99, 987.77, 1174.66].forEach(freq => {
        playTone(freq, 100, 'triangle', 0.5, 175);
    });
};

export const playStopSound = () => {
  // G Major to C Major, a common V-I cadence for a feeling of resolution.
  const firstChord = [392.00, 493.88, 587.33]; // G4 Major
  const secondChord = [261.63, 329.63, 392.00]; // C4 Major
  
  firstChord.forEach(freq => playTone(freq, 150, 'sine', 0.6));
  setTimeout(() => {
    secondChord.forEach(freq => playTone(freq, 250, 'sine', 0.6));
  }, 160);
};

export const playIntroTheme = () => {
    const volume = 0.3;
    const waveType: ToneType = 'sawtooth';
    const noteDuration = 80;
    let delay = 0;

    const notes = {
        C4: 261.63,  Eb4: 311.13,  G4: 392.00,
        Ab3: 207.65, C4_2: 261.63, Eb4_2: 311.13,
        F3: 174.61
    };

    const cMinorOstinato = [
        notes.C4, notes.Eb4, notes.G4, notes.Eb4,
        notes.C4, notes.Eb4, notes.G4, notes.Eb4,
    ];

    const abMajorOstinato = [
        notes.Ab3, notes.C4_2, notes.Eb4_2, notes.C4_2,
        notes.Ab3, notes.C4_2, notes.Eb4_2, notes.C4_2,
    ];

    const fMinorOstinato = [
        notes.F3, notes.Ab3, notes.C4_2, notes.Ab3,
        notes.F3, notes.Ab3, notes.C4_2, notes.Ab3,
    ];

    const sequence = [
        ...cMinorOstinato,
        ...abMajorOstinato,
        ...fMinorOstinato,
        ...cMinorOstinato,
    ];

    sequence.forEach(freq => {
        if (!isFinite(freq)) return;
        playTone(freq, noteDuration, waveType, volume, delay);
        delay += noteDuration;
    });

    // Final chord for a powerful finish
    const finalChord = [notes.C4, notes.Eb4, notes.G4];
    finalChord.forEach(freq => {
        if (!isFinite(freq)) return;
        playTone(freq, 1800, waveType, volume * 1.2, delay);
    });
};

export const play100kmhSound = () => {
    const volume = 0.6;
    const waveType: ToneType = 'sawtooth';

    // A fast, rising C Major 7th arpeggio, lowered by one octave
    const notes = [261.63, 329.63, 392.00, 493.88]; // C4, E4, G4, B4
    let delay = 0;
    const noteDuration = 80;

    notes.forEach(freq => {
        playTone(freq, noteDuration, waveType, volume, delay);
        delay += noteDuration;
    });

    // Followed by a triumphant higher C Major chord, lowered by one octave
    const finalChord = [523.25, 659.25, 783.99]; // C5, E5, G5
    finalChord.forEach(freq => {
        playTone(freq, 400, waveType, volume * 1.1, delay);
    });
};


// Kept for reference, but aliased
export const playMarkerSound = playMarkerStartSound;



